import jsPDF from 'jspdf';
import { Cursor } from 'lib/classes/jsPdfCursor';
import { formatDate, formatMoney, validateProducts } from 'lib/functions/utils';
import { User } from 'services/redux/authSlice';

import { OperativeProfileData } from '../index';

export const generateOperativeProfilePDF = (
  data: OperativeProfileData,
  imageBase64: string,
  user?: User,
) => {
  const showSgr = validateProducts(user, ['sgr']);
  const showCredit = validateProducts(user, ['credit']);
  const showConfirming = validateProducts(user, ['confirming']);
  const showFactoring = validateProducts(user, ['factoring']);

  const doc = new jsPDF();
  const c = new Cursor(doc);
  c.col12 = 60;
  c.col22 = 160;
  let yPosition = 20;

  // Título principal
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.text('Perfil Operativo', 105, yPosition, { align: 'center' });
  yPosition += 10;

  doc.setFontSize(14);
  doc.setFont('helvetica', 'normal');
  doc.text(data.businessName, 105, yPosition, { align: 'center' });
  yPosition += 15;

  c.y = yPosition;

  c.writeTitle('Resumen de Cuenta Corriente');

  const tempY = c.y;
  c.writeLine('Monto a Favor', true, 'col11');
  c.writeLineAndNewLine(formatMoney(data.inFavorTotal ?? 0), true, 'col12');
  for (const account of data.inFavorAccounts ?? []) {
    c.writeLine(account.name, false, 'col11');
    c.writeLineAndNewLine(formatMoney(account.total ?? 0), false, 'col12');
  }
  c.newLine();
  let tempY2 = c.y;

  c.moveYTo(tempY);
  c.writeLine('Monto en Contra', true, 'col21');
  c.writeLineAndNewLine(formatMoney(data.againstTotal ?? 0), true, 'col22');
  for (const account of data.againstAccounts ?? []) {
    if (account.total && account.total > 0) {
      c.writeLine(account.name, false, 'col21');
      c.writeLineAndNewLine(formatMoney(account.total ?? 0), false, 'col22');
    }
  }
  c.newLine();
  tempY2 = c.y > tempY ? c.y : tempY;

  c.moveYTo(tempY2);
  c.writeLine('Moras', true, 'col11');
  c.writeLineAndNewLine(formatMoney(data.arrearsTotal ?? 0), true, 'col12');
  for (const account of data.arrearsAccounts ?? []) {
    if (account.total && account.total > 0) {
      c.writeLine(account.name, false, 'col11');
      c.writeLineAndNewLine(formatMoney(account.total ?? 0), false, 'col12');
    }
  }
  c.newLine();

  c.writeTitle('Líneas de Crédito');

  if (showFactoring) {
    c.writeLine('Factoring', true, 'col11');
    c.writeLineAndNewLine(
      formatMoney(data.factoringCreditLine?.amount ?? 0),
      true,
      'col12',
    );
    if (data.factoringCreditLine) {
      c.writeLine('Estado', false, 'col11');
      c.writeLine(data.factoringCreditLine?.status ?? '', false, 'col12');
      c.writeLine('Utilizado', false, 'col21');
      c.writeLineAndNewLine(
        formatMoney(data.factoringCreditLineUsedAmount ?? 0),
        false,
        'col22',
      );
      c.writeLine('Válida hasta', false, 'col11');
      c.writeLine(
        data.factoringCreditLine?.validUntil
          ? formatDate(data.factoringCreditLine.validUntil)
          : '',
        false,
        'col12',
      );
      c.writeLine('Disponible', false, 'col21');
      c.writeLineAndNewLine(
        formatMoney(
          data.factoringCreditLine.amount - data.factoringCreditLineUsedAmount,
        ),
        false,
        'col22',
      );
    } else {
      c.writeLineAndNewLine(
        'No hay línea de crédito disponible',
        false,
        'col11',
      );
    }
    c.newLine();
  }

  if (showConfirming) {
    c.writeLine('Confirming', true, 'col11');
    c.writeLineAndNewLine(
      formatMoney(data.confirmingCreditLine?.amount ?? 0),
      true,
      'col12',
    );
    if (data.confirmingCreditLine) {
      c.writeLine('Estado', false, 'col11');
      c.writeLine(data.confirmingCreditLine?.status ?? '', false, 'col12');
      c.writeLine('Utilizado', false, 'col21');
      c.writeLineAndNewLine(
        formatMoney(data.confirmingCreditLineUsedAmount ?? 0),
        false,
        'col22',
      );
      c.writeLine('Válida hasta', false, 'col11');
      c.writeLine(
        data.confirmingCreditLine?.validUntil
          ? formatDate(data.confirmingCreditLine.validUntil)
          : '',
        false,
        'col12',
      );
      c.writeLine('Disponible', false, 'col21');
      c.writeLineAndNewLine(
        formatMoney(
          data.confirmingCreditLine.amount -
            data.confirmingCreditLineUsedAmount,
        ),
        false,
        'col22',
      );
    } else {
      c.writeLineAndNewLine(
        'No hay línea de crédito disponible',
        false,
        'col11',
      );
    }
    c.newLine();
  }

  if (showCredit) {
    c.writeLine('Créditos', true, 'col11');
    c.writeLineAndNewLine(
      formatMoney(data.creditCreditLine?.amount ?? 0),
      true,
      'col12',
    );
    if (data.creditCreditLine && showCredit) {
      c.writeLine('Estado', false, 'col11');
      c.writeLine(data.creditCreditLine?.status ?? '', false, 'col12');
      c.writeLine('Utilizado', false, 'col21');
      c.writeLineAndNewLine(
        formatMoney(data.creditCreditLineUsedAmount ?? 0),
        false,
        'col22',
      );
      c.writeLine('Válida hasta', false, 'col11');
      c.writeLine(
        data.creditCreditLine?.validUntil
          ? formatDate(data.creditCreditLine.validUntil)
          : '',
        false,
        'col12',
      );
      c.writeLine('Disponible', false, 'col21');
      c.writeLineAndNewLine(
        formatMoney(
          data.creditCreditLine.amount - data.creditCreditLineUsedAmount,
        ),
        false,
        'col22',
      );
    } else {
      c.writeLineAndNewLine(
        'No hay línea de crédito disponible',
        false,
        'col11',
      );
    }
    c.newLine();
  }

  if (showSgr) {
    c.writeLine('Garantías', true, 'col11');
    c.writeLineAndNewLine(
      formatMoney(data.sgrCreditLine?.amount ?? 0),
      true,
      'col12',
    );
    if (data.sgrCreditLine) {
      c.writeLine('Estado', false, 'col11');
      c.writeLine(data.sgrCreditLine?.status ?? '', false, 'col12');
      c.writeLine('Utilizado', false, 'col21');
      c.writeLineAndNewLine(
        formatMoney(data.sgrCreditLineUsage.totalUsage ?? 0),
        false,
        'col22',
      );
      c.writeLine('Válida hasta', false, 'col11');
      c.writeLine(
        data.sgrCreditLine?.validUntil
          ? formatDate(data.sgrCreditLine.validUntil)
          : '',
        false,
        'col12',
      );
      c.writeLine('Disponible', false, 'col21');
      c.writeLineAndNewLine(
        formatMoney(
          data.sgrCreditLine.amount - data.sgrCreditLineUsage.totalUsage,
        ),
        false,
        'col22',
      );

      c.writeLine('Fiel Cumplimiento', false, 'col11');
      c.writeLine(
        `${formatMoney(data.sgrCreditLineUsage.faithfulComplianceUsage)} de ${formatMoney(
          data.sgrCreditLine.subProducts?.find(
            (sub) => sub.name === 'Fiel cumplimiento',
          )?.amount || 0,
        )}`,
        false,
        'col12',
      );
      c.writeLine('Seriedad de la Oferta', false, 'col21');
      c.writeLineAndNewLine(
        `${formatMoney(data.sgrCreditLineUsage.offerSeriousnessUsage)} de ${formatMoney(
          data.sgrCreditLine.subProducts?.find(
            (sub) => sub.name === 'Seriedad de oferta',
          )?.amount || 0,
        )}`,
        false,
        'col22',
      );
    } else {
      c.writeLineAndNewLine(
        'No hay línea de crédito disponible',
        false,
        'col11',
      );
    }
    c.newLine();
  }

  c.writeTitle('Documentos');
  if (data.factoringDocumentsAsClient.length > 0) {
    c.writeLineAndNewLine('Factoring (cliente)', true, 'col11');
    c.writeTable(
      [
        'Deudor',
        'Rut deudor',
        'Folio',
        'Monto',
        'Conciliado',
        'Mora',
        'Emisión',
        'Vencimiento',
      ],
      data.factoringDocumentsAsClient.map((doc: any) => [
        doc.stakeholderName,
        doc.stakeholderIdentifier,
        doc.folio,
        formatMoney(doc.amount),
        formatMoney(doc.amountConciliated),
        formatMoney(doc.arrearAmount),
        formatDate(doc.issuedDate),
        formatDate(doc.paymentDate),
      ]),
    );
  }
  if (data.factoringDocumentsAsStakeholder.length > 0) {
    c.writeLineAndNewLine('Factoring (deudor)', true, 'col11');
    c.writeTable(
      ['Cliente', 'Rut cliente', 'Folio', 'Monto', 'Emisión', 'Vencimiento'],
      data.factoringDocumentsAsStakeholder.map((doc: any) => [
        doc.businessName,
        doc.businessIdentifier,
        doc.folio,
        formatMoney(doc.amount),
        formatDate(doc.issuedDate),
        formatDate(doc.paymentDate),
      ]),
    );
  }
  if (data.creditDocuments.length > 0) {
    c.writeLineAndNewLine('Créditos', true, 'col11');
    c.writeTable(
      ['Folio', 'Monto', 'Conciliado', 'Mora', 'Vencimiento'],
      data.creditDocuments.map((doc: any) => [
        doc.folio,
        formatMoney(doc.amount),
        formatMoney(doc.amountConciliated),
        formatMoney(doc.arrearAmount),
        formatDate(doc.paymentDate),
      ]),
    );
  }
  if (data.confirmingDocuments.length > 0) {
    c.writeLineAndNewLine('Confirming', true, 'col11');
    c.writeTable(
      [
        'Cliente',
        'Rut cliente',
        'Folio',
        'Monto',
        'Conciliado',
        'Mora',
        'Emisión',
        'Vencimiento',
      ],
      data.confirmingDocuments.map((doc: any) => [
        doc.businessName,
        doc.businessIdentifier,
        doc.folio,
        formatMoney(doc.amount),
        formatMoney(doc.amountConciliated),
        formatMoney(doc.arrearAmount),
        formatDate(doc.issuedDate),
        formatDate(doc.paymentDate),
      ]),
    );
  }
  if (data?.sgrDocuments?.length > 0) {
    c.writeLineAndNewLine('Garantías', true, 'col11');
    c.writeTable(
      ['Folio', 'Cliente', 'Rut cliente', 'Monto', 'Conciliado', 'Emisión'],
      data.sgrDocuments.map((doc: any) => [
        doc.folio,
        doc.businessName,
        doc.businessIdentifier,
        formatMoney(doc.amount),
        formatMoney(doc.amountConciliated),
        formatDate(doc.issuedDate),
      ]),
    );
  }
  if (data.pendingAccountsInFavor.length > 0) {
    c.writeLineAndNewLine('Cuentas por pagar', true, 'col11');
    c.writeTable(
      ['Id', 'Categoría', 'Observación', 'Fecha', 'Monto'],
      data.pendingAccountsInFavor.map((doc: any) => [
        doc.folio,
        doc.pendingAccountType,
        doc.pendingAccountObservation,
        formatDate(doc.issuedDate),
        formatMoney(doc.amount),
      ]),
    );
  }
  if (data.pendingAccountsAgainst.length > 0) {
    c.writeLineAndNewLine('Cuentas por cobrar', true, 'col11');
    c.writeTable(
      ['Id', 'Categoría', 'Observación', 'Fecha', 'Monto'],
      data.pendingAccountsAgainst.map((doc: any) => [
        doc.folio,
        doc.pendingAccountType,
        doc.pendingAccountObservation,
        formatDate(doc.issuedDate),
        formatMoney(doc.amount),
      ]),
    );
  }
  // Pie de página
  const pageCount = (doc as any).internal.getNumberOfPages();
  const imgHeight = 5;

  const aspectRatio = 185 / 53;
  const imgWidth = imgHeight * aspectRatio;
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(
      `Página ${i} de ${pageCount}`,
      105,
      doc.internal.pageSize.height - 10,
      { align: 'center' },
    );
    doc.text(
      `Generado el: ${formatDate(new Date())}`,
      20,
      doc.internal.pageSize.height - 10,
    );
    doc.addImage(
      imageBase64,
      'PNG',
      doc.internal.pageSize.width - 5 - imgWidth,
      doc.internal.pageSize.height - 5 - imgHeight,
      imgWidth,
      imgHeight,
    );
  }

  // Guardar el PDF
  const fileName = `Perfil_Operativo_${data.businessName?.replace(/ /g, '_') || 'Cliente'}_${new Date().toISOString().split('T')[0]}.pdf`;
  doc.save(fileName);
};
